import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Panel } from "@/components/shared";
import { BanksEditor, LabelValueEditor } from "@/components/settings/TemplateEditors";
import { api, errMsg, useApi } from "@/lib/api";

function Field({ label, children, hint }) {
  return <div className="space-y-1.5"><Label>{label}</Label>{children}{hint && <p className="text-xs text-muted-foreground">{hint}</p>}</div>;
}

export function QuoteTemplateTab() {
  const { data } = useApi("/settings/quote-template");
  const [f, setF] = useState(null);
  useEffect(() => { if (data) setF((cur) => cur || data); }, [data]);
  if (!f) return null;
  const set = (k) => (v) => setF({ ...f, [k]: v?.target ? v.target.value : v });
  const save = async () => {
    try { setF((await api.put("/settings/quote-template", { ...f, validityDays: +f.validityDays })).data); toast.success("Quotation template saved"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const reset = async () => {
    if (!window.confirm("Reset the quotation template to the official defaults?")) return;
    try { setF((await api.post("/settings/quote-template/reset")).data); toast.success("Template reset to defaults"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-6" data-testid="quote-template-tab">
      <Panel title="Company & validity"><div className="p-5 grid sm:grid-cols-2 gap-4">
        <Field label="Office address"><Textarea rows={5} value={f.companyAddress} onChange={set("companyAddress")} data-testid="template-address-input" /></Field>
        <div className="space-y-4">
          <Field label="Phone"><Input value={f.companyPhone} onChange={set("companyPhone")} data-testid="template-phone-input" /></Field>
          <Field label="Company GSTIN (shown on B2B invoices)"><Input value={f.companyGstin || ""} onChange={set("companyGstin")} data-testid="template-gstin-input" /></Field>
          <Field label="Quotation validity (days)" hint="Expiry date = quote date + these days."><Input type="number" value={f.validityDays} onChange={set("validityDays")} data-testid="template-validity-input" /></Field>
        </div>
        <Field label="Free inclusion row (label)"><Input value={f.freeInclusionLabel} onChange={set("freeInclusionLabel")} data-testid="template-free-label-input" /></Field>
        <Field label="Free inclusion row (part)"><Input value={f.freeInclusionValue} onChange={set("freeInclusionValue")} data-testid="template-free-value-input" /></Field>
        <Field label="GST note"><Input value={f.gstNote} onChange={set("gstNote")} data-testid="template-gst-note-input" /></Field>
        <Field label="Reference image note"><Input value={f.referenceImageNote} onChange={set("referenceImageNote")} data-testid="template-ref-note-input" /></Field>
      </div></Panel>
      <Panel title="Base PC Build Package"><div className="p-5"><LabelValueEditor rows={f.basePackage} onChange={set("basePackage")} testid="template-base" /></div></Panel>
      <Panel title="GN Privilege package perks"><div className="p-5"><LabelValueEditor rows={f.privilegePerks} onChange={set("privilegePerks")} testid="template-perks" /></div></Panel>
      <Panel title="Payment details"><div className="p-5 space-y-4">
        <BanksEditor banks={f.banks} onChange={set("banks")} />
        <Field label="UPI ID (shown with the QR code)"><Input value={f.upiId} onChange={set("upiId")} className="sm:w-80" data-testid="template-upi-input" /></Field>
        <Field label="Payment terms" hint="Use {{advance_amount}} and {{final_amount}} for the 50% amounts. Text before a colon is shown in bold.">
          <Textarea rows={10} value={f.paymentTerms} onChange={set("paymentTerms")} data-testid="template-payment-terms-input" /></Field>
      </div></Panel>
      <Panel title="Terms and conditions"><div className="p-5">
        <Textarea rows={18} value={f.terms} onChange={set("terms")} className="font-mono text-xs" data-testid="template-terms-input" />
        <p className="text-xs text-muted-foreground mt-1.5">Numbered lines ("1. Title:") become headings; lines starting with "- " become bullet points.</p>
      </div></Panel>
      <div className="flex gap-2"><Button onClick={save} data-testid="save-quote-template-button">Save template</Button>
        <Button variant="outline" onClick={reset} data-testid="reset-quote-template-button">Reset to defaults</Button></div>
    </div>
  );
}
